import type { NoticeInput } from '../ui/notifications';
import { useCallback, useEffect, useState } from 'react';
import { hexA, type InviteRole, type InviteSummary, type MemberInfo } from '@scribbleplot/shared';
import { ApiError, api } from '../lib/api';
import { color, font, shadow } from '../tokens';
import { useBoard } from './store';
import { AccentButton, Button, TextInput, Toggle } from '../ui/primitives';

/**
 * Sharing and collaborators.
 *
 * Two ways in, because they solve different problems: a public link for
 * read-only viewing, and per-person access for people who need to edit. There
 * is no mail transport in a self-hosted deployment, so an invite link the owner
 * passes along is the primary route — adding by email only works for people who
 * already have an account here.
 */

const ROLES: { value: InviteRole; label: string; hint: string }[] = [
  { value: 'editor', label: 'Can edit', hint: 'Add, move, and delete anything on the board' },
  { value: 'viewer', label: 'Can view', hint: 'Read-only, and can leave comments' },
];

export function SharePanel({
  boardId,
  accent,
  isPublic,
  shareUrl,
  onTogglePublic,
  onCopy,
  onNotify,
}: {
  boardId: string;
  accent: string;
  isPublic: boolean;
  shareUrl: string;
  onTogglePublic: (next: boolean) => void;
  onCopy: (text: string, label: string) => void;
  onNotify: (message: NoticeInput) => void;
}) {
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [role, setRole] = useState<InviteRole>('editor');
  const [email, setEmail] = useState('');
  const [freshLink, setFreshLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { members: m, invites: i } = await api.listCollaborators(boardId);
      setMembers(m);
      setInvites(i);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  /**
   * Someone accepting an invite shows up as a peer on the board, so the roster
   * doubles as a signal that the member list has changed. Without this the
   * panel would keep showing whoever was a member when it was opened.
   */
  const peerCount = useBoard((state) => state.peers.length);

  useEffect(() => {
    void load();
  }, [load, peerCount]);

  const guard = async (work: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await work();
    } catch (err) {
      onNotify({ kind: 'error', message: err instanceof ApiError ? err.message : 'Something went wrong.' });
    } finally {
      setBusy(false);
    }
  };

  const mintLink = () =>
    guard(async () => {
      const { invite } = await api.createInvite(boardId, role);
      // Shown once — the server keeps only a hash of the token.
      setFreshLink(invite.url);
      onCopy(invite.url, 'Invite link copied');
      await load();
    });

  const addByEmail = () =>
    guard(async () => {
      const target = email.trim();
      if (!target) return;
      const { members: next } = await api.addMember(boardId, target, role);
      setMembers(next);
      setEmail('');
      onNotify(`${target} can now ${role === 'editor' ? 'edit' : 'view'} this board`);
    });

  const changeRole = (userId: string, next: InviteRole) =>
    guard(async () => {
      const { members: updated } = await api.setMemberRole(boardId, userId, next);
      setMembers(updated);
    });

  const remove = (member: MemberInfo) =>
    guard(async () => {
      const { members: updated } = await api.removeMember(boardId, member.userId);
      setMembers(updated);
      onNotify(`Removed ${member.name}`);
    });

  const revoke = (invite: InviteSummary) =>
    guard(async () => {
      const { invites: updated } = await api.revokeInvite(boardId, invite.id);
      setInvites(updated);
      if (freshLink) setFreshLink(null);
      onNotify('Invite link revoked');
    });

  const label = { fontSize: 10, fontFamily: font.mono, letterSpacing: '.12em', color: color.textFaint } as const;
  const mono = {
    flex: 1,
    minWidth: 0,
    background: color.input,
    border: `1px solid rgba(255,255,255,.1)`,
    borderRadius: 8,
    padding: '8px 9px',
    fontFamily: font.mono,
    fontSize: 11,
    color: '#B9BCC7',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <div
      onPointerDown={(event) => event.stopPropagation()}
      style={{
        position: 'absolute',
        right: 0,
        top: 38,
        width: 340,
        maxHeight: 'min(72vh, 620px)',
        overflowY: 'auto',
        background: color.panel,
        border: `1px solid ${color.lineStrong}`,
        borderRadius: 14,
        padding: 15,
        zIndex: 9998,
        boxShadow: shadow.panel,
        animation: 'sp-pop-in .14s ease',
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 700 }}>Invite collaborators</div>
      <p className="sp-description" style={{ fontSize: 12, marginTop: 6 }}>
        Choose who can edit or view this board. Public-link access is managed separately below.
      </p>
      {loading && <p role="status">Loading collaborators…</p>}
      {loadError && (
        <div role="alert" className="sp-error">
          Could not load collaborators.{' '}
          <button className="sp-button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {/* People with named access */}
      <div style={{ ...label, marginTop: 15 }}>INVITE PEOPLE</div>

      <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
        {ROLES.map((option) => {
          const active = role === option.value;
          return (
            <Button
              key={option.value}
              onClick={() => setRole(option.value)}
              title={option.hint}
              ariaPressed={active}
              hoverStyle={{ background: active ? hexA(accent, 0.2) : color.hover }}
              style={{
                flex: 1,
                height: 30,
                borderRadius: 8,
                border: `1px solid ${active ? accent : color.lineStrong}`,
                background: active ? hexA(accent, 0.15) : 'transparent',
                color: color.textStrong,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {option.label}
            </Button>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 7, marginTop: 9 }}>
        <TextInput
          value={email}
          onChange={setEmail}
          placeholder="teammate@company.com"
          type="email"
          ariaLabel="Invite by email"
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === 'Enter') void addByEmail();
          }}
          style={{ height: 32, fontSize: 12.5 }}
        />
        <AccentButton accent={accent} size="sm" onClick={() => void addByEmail()} disabled={busy || !email.trim()}>
          Add
        </AccentButton>
      </div>

      <Button
        onClick={() => void mintLink()}
        disabled={busy}
        hoverStyle={{ background: color.hover }}
        style={{
          width: '100%',
          height: 32,
          marginTop: 8,
          borderRadius: 8,
          border: `1px dashed ${color.lineStrong}`,
          background: 'transparent',
          color: color.textStrong,
          fontSize: 12.5,
          fontWeight: 600,
        }}
      >
        + Create an invite link
      </Button>

      {freshLink && (
        <div style={{ display: 'flex', gap: 7, marginTop: 9 }}>
          <div style={mono} title={freshLink}>
            {freshLink}
          </div>
          <Button
            onClick={() => onCopy(freshLink, 'Invite link copied')}
            style={{
              height: 33,
              padding: '0 12px',
              borderRadius: 8,
              border: 'none',
              background: accent,
              color: color.onAccent,
              fontSize: 12,
              fontWeight: 700,
              flex: 'none',
            }}
          >
            Copy
          </Button>
        </div>
      )}

      {members.map((member) => (
        <div key={member.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: color.textStrong }}>{member.name}</div>
            <div
              style={{
                fontSize: 11,
                color: color.textDim,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {member.email}
            </div>
          </div>
          <select
            value={member.role}
            aria-label={`Role for ${member.name}`}
            onChange={(event) => void changeRole(member.userId, event.target.value as InviteRole)}
            style={{
              height: 26,
              background: color.input,
              color: color.textStrong,
              border: `1px solid ${color.lineStrong}`,
              borderRadius: 7,
              fontSize: 11.5,
              fontFamily: 'inherit',
              padding: '0 4px',
            }}
          >
            <option value="editor">Can edit</option>
            <option value="viewer">Can view</option>
          </select>
          <Button
            onClick={() => void remove(member)}
            title={`Remove ${member.name}`}
            ariaLabel={`Remove ${member.name}`}
            hoverStyle={{ background: 'rgba(248,113,113,.12)' }}
            style={{
              width: 24,
              height: 24,
              borderRadius: 7,
              border: 'none',
              background: 'transparent',
              color: color.textDim,
              fontSize: 14,
              lineHeight: 1,
              padding: 0,
              flex: 'none',
            }}
          >
            ×
          </Button>
        </div>
      ))}

      {invites.length > 0 && (
        <>
          <div style={{ ...label, marginTop: 15 }}>ACTIVE INVITE LINKS</div>
          {invites.map((invite) => (
            <div key={invite.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: color.textStrong }}>
                  {invite.role === 'editor' ? 'Can edit' : 'Can view'}
                </div>
                <div style={{ fontSize: 10.5, fontFamily: font.mono, color: color.textDim }}>
                  used {invite.uses}/{invite.maxUses} · expires {new Date(invite.expiresAt).toLocaleDateString()}
                </div>
              </div>
              <Button
                onClick={() => void revoke(invite)}
                style={{
                  height: 24,
                  padding: '0 9px',
                  borderRadius: 7,
                  border: `1px solid rgba(248,113,113,.35)`,
                  background: 'transparent',
                  color: color.danger,
                  fontSize: 11,
                  fontWeight: 600,
                  flex: 'none',
                }}
              >
                Revoke
              </Button>
            </div>
          ))}
        </>
      )}

      {/* Public read-only link */}
      <div aria-hidden style={{ height: 1, background: 'rgba(255,255,255,.08)', margin: '15px 0 0' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 13 }}>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: color.textStrong }}>Public link</div>
          <div style={{ fontSize: 11, color: color.textDim, marginTop: 1 }}>Anyone with the link can view</div>
        </div>
        <Toggle on={isPublic} onChange={onTogglePublic} accent={accent} label="Public link" />
      </div>

      {isPublic && (
        <div style={{ display: 'flex', gap: 7, marginTop: 13 }}>
          <div style={mono} title={shareUrl}>
            {shareUrl}
          </div>
          <Button
            onClick={() => onCopy(shareUrl, 'Link copied')}
            style={{
              height: 33,
              padding: '0 12px',
              borderRadius: 8,
              border: 'none',
              background: accent,
              color: color.onAccent,
              fontSize: 12,
              fontWeight: 700,
              flex: 'none',
            }}
          >
            Copy
          </Button>
        </div>
      )}
    </div>
  );
}
